import { useState, useEffect, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Upload } from "lucide-react";

interface GalleryImage {
  id: number;
  title: string | null;
  caption: string | null;
  imagePath: string;
  sortOrder: number;
  createdAt: string;
}

export default function GalleryManager() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    loadImages();
  }, []);

  async function loadImages() {
    try {
      const data = await apiFetch("/gallery/images");
      setImages(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError("Please select a file");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const urlRes = await apiFetch("/storage/uploads/request-url", {
        method: "POST",
        body: JSON.stringify({
          name: file.name,
          size: file.size,
          contentType: file.type,
        }),
      });

      await fetch(urlRes.uploadURL, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      await apiFetch("/gallery/images", {
        method: "POST",
        body: JSON.stringify({
          title: title || null,
          caption: caption || null,
          imagePath: urlRes.objectPath,
          sortOrder: images.length,
        }),
      });

      await loadImages();
      setShowAdd(false);
      setTitle("");
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this image?")) return;
    try {
      await apiFetch(`/gallery/images/${id}`, { method: "DELETE" });
      await loadImages();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function getImageUrl(imagePath: string) {
    return `${BASE}/api/storage${imagePath}`;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-serif font-bold">Photo Gallery</h2>
        <Button onClick={() => setShowAdd(!showAdd)} style={{ borderRadius: "16px 0 16px 0" }}>
          <Plus className="w-4 h-4 mr-1" /> Add Photo
        </Button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}

      {showAdd && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h3 className="font-semibold mb-4">Upload a New Photo</h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label htmlFor="file">Photo</Label>
              <input
                ref={fileInputRef}
                type="file"
                id="file"
                accept="image/*"
                required
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              />
            </div>
            <div>
              <Label htmlFor="photoTitle">Title (optional)</Label>
              <Input id="photoTitle" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Give your photo a title" />
            </div>
            <div>
              <Label htmlFor="photoCaption">Caption (optional)</Label>
              <Input id="photoCaption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} style={{ borderRadius: "16px 0 16px 0" }}>
                <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading..." : "Upload"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading gallery...</p>
      ) : images.length === 0 ? (
        <div className="bg-white p-12 text-center rounded-lg shadow-sm">
          <p className="text-muted-foreground mb-4">No photos in the gallery yet. Upload your first one!</p>
          <Button onClick={() => setShowAdd(true)} style={{ borderRadius: "16px 0 16px 0" }}>
            <Plus className="w-4 h-4 mr-1" /> Add Photo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow-sm overflow-hidden group">
              <div className="aspect-square relative">
                <img
                  src={getImageUrl(image.imagePath)}
                  alt={image.title || "Gallery image"}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleDelete(image.id)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {(image.title || image.caption) && (
                <div className="p-3">
                  {image.title && <p className="font-semibold text-sm">{image.title}</p>}
                  {image.caption && <p className="text-xs text-muted-foreground">{image.caption}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
